import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledDateOnlyPicker, ControlledNumberField, ControlledSelect, ControlledTextField } from '@if/mui'
import { Alert, Stack } from '@mui/material'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { useTranslatedFeeTypeList } from '@src/types/constants'
import { OperationInformation } from '@src/types/loan/loan-operation'
import { DateOnly } from 'node_modules/@if/core/dist/date/dateOnly'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AddFeeSchemaType, getAddFeeSchema } from './AddFee-schema'

interface AddFeeDialogProps {
  onConfirm: (fee: AddFeeSchemaType) => void
  onClose: () => void
  isOpen: boolean
  operationInformations: OperationInformation[]
}

const AddFeeDialog = ({ onConfirm, onClose, isOpen, operationInformations }: AddFeeDialogProps) => {
  const { t } = useTranslation()

  const resolver = useMemo(() => yupResolver(getAddFeeSchema(operationInformations)), [operationInformations])

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<AddFeeSchemaType>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      feeType: undefined,
      amount: undefined,
      effectiveDate: undefined,
      note: '',
    },
  })

  const { dialogProps } = useUnmountableDialog(onClose)

  const handleOnConfirm = handleSubmit((data) => {
    onConfirm(data)
  })

  const feeTypeOptions = useTranslatedFeeTypeList()

  return (
    <FormDialog
      {...dialogProps}
      disabled={!isValid}
      title={t('loan.feesDialog.addFee')}
      open={isOpen}
      onClose={onClose}
      onConfirm={handleOnConfirm}
    >
      <Stack spacing={2} marginTop={1}>
        {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
        <ControlledSelect
          control={control}
          name="feeType"
          options={feeTypeOptions}
          label={t('loan.feesDialog.feeType')}
          disableEmptyOption
        />
        <ControlledNumberField
          control={control}
          name="amount"
          label={t('loan.feesDialog.amount')}
          size="small"
          enableNegative
        />
        <ControlledDateOnlyPicker
          control={control}
          name="effectiveDate"
          label={t('loan.feesDialog.effectiveDate')}
          slotProps={{ textField: { size: 'small' } }}
          minDate={new DateOnly()}
        />
        <ControlledTextField control={control} name="note" label={t('loan.feesDialog.note')} size="small" />
      </Stack>
    </FormDialog>
  )
}

export default AddFeeDialog
